import { useState } from 'react'
import {
  Control,
  Controller,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form'
import { FormInput } from '../../../../../components/forms/input/FormInput'
import { FormSelect } from '../../../../../components/forms/select/FormSelect'
import { Opcion } from '../../../../../models/catalogo.type'
import { useRecuperarOpcCatalogoQuery } from '../../../../../redux/api'
import { AppCodigosCatalogo, AppConstantes, AppMapas } from '../../../../../utils/Constantes'
import { Condicionante } from '../../../models'
import { TCondicionanteForm } from '../../../models/condicionanteForm.type'
import FormTextArea from '../../../../../components/forms/textArea/FormTextArea'

type Props = {
  fechaVencimientoAutorizacion: string | undefined
  isModoEditar: boolean
  calcularCantidadTareas: (arg: any) => void
  borrarElementosTareasNoManuales: () => void
  condicionante?: Condicionante
  control: Control<TCondicionanteForm>
  errors: any
  register: UseFormRegister<TCondicionanteForm>
  getValues: UseFormGetValues<TCondicionanteForm>
  setValue: UseFormSetValue<TCondicionanteForm>
  usuarioPermiso?: boolean
}
export const DatosCondicionanteModal = ({
  fechaVencimientoAutorizacion,
  isModoEditar,
  calcularCantidadTareas,
  borrarElementosTareasNoManuales,
  condicionante,
  control,
  errors,
  register,
  getValues,
  setValue,
  usuarioPermiso,
}: Props) => {
  // Datos generales de la condicionante.
  // En creacion, periodicidad+cantidadPeriodos gobiernan la generacion automatica de tareas.
  // En edicion, periodicidad se muestra en solo lectura (no se regeneran tareas historicas).
  const { data: catEstatus } = useRecuperarOpcCatalogoQuery(
    AppCodigosCatalogo.ESTATUS_CONDICIONANTE
  )
  const { data: catPeriodicidad } = useRecuperarOpcCatalogoQuery(
    AppCodigosCatalogo.PERIODICIDAD_CONDICIONANTE
  )

  const [periodicidadCondicionante, setPeriodicidadCondicionante] = useState({})
  const [nombreCondicionante, setNombreCondicionante] = useState(condicionante?.nombre ?? '')
  const [descripcionCondicionante, setDescripcionCondicionante] = useState(
    condicionante?.descripcion ?? ''
  )
  /**
   * @description genera el texto para el prellenado del titulo tarea.
   *
   * @author ana.carrasco  Hábil MX
   * @version 1.0.0, 31/05/2023
   */
  const generarTextoTituloTarea = () => {
    // Si existen tareas autogeneradas (isManual=false), sincroniza el prefijo del titulo
    // cuando el usuario cambia el nombre de la condicionante.
    const tamanioArregloTareas = getValues('tareas').length
    for (let i = 0; i < tamanioArregloTareas; i++) {
      if (!getValues('tareas')[i].idTarea && !getValues('tareas')[i].isManual) {
        const isfecha = /(-{1}\s\d{1,2}\/\w{1,4}\/\d{2,4})/gm
        const testIsFecha = isfecha.test(getValues('tareas')[i].titulo)
        if (testIsFecha) {
          setValue(
            `tareas.${i}.titulo`,
            `${getValues('tareas')[i].titulo.replace(
              nombreCondicionante,
              `${getValues('nombre')}`
            )}`
          )
        }
      }
    }
  }

  /**
   * @description genera el texto para el prellenado de la descripción tarea.
   *
   * @author ana.carrasco  Hábil MX
   * @version 1.0.0, 15/09/2023
   */
  const generarTextoDescripcionTarea = () => {
    // Sincroniza la descripcion en tareas autogeneradas cuando cambia la descripcion padre.
    const tamanioArregloTareas = getValues('tareas').length
    for (let i = 0; i < tamanioArregloTareas; i++) {
      if (!getValues('tareas')[i].idTarea && !getValues('tareas')[i].isManual) {
        setValue(
          `tareas.${i}.descripcion`,
          `${getValues('tareas')[i].descripcion.replace(
            descripcionCondicionante,
            `${getValues('descripcion')}`
          )}`
        )
      }
    }
  }
  return (
    <>
      <div className='col-12 mb-4'>
        <h5>Datos generales</h5>
      </div>
      <div className='row'>
        <div className='col-12  d-flex'>
          <div style={{ width: '25%' }} className='me-3'>
            <div className='d-flex'>
              <div className='flex-grow-1'>
                <Controller
                  control={control}
                  name='nombre'
                  render={({ field: { onChange } }) => (
                    <FormInput
                      disabled={!usuarioPermiso}
                      maxLength={100}
                      isObligatorio
                      className='mb-5'
                      nombreCampo='nombre'
                      nombre='Nombre'
                      register={register}
                      errors={errors}
                      placeholder='Ingresa el nombre'
                      onChange={(e: string) => {
                        onChange(e)
                        generarTextoTituloTarea()
                        setNombreCondicionante(getValues('nombre'))
                      }}
                    />
                  )}
                />
              </div>
            </div>
          </div>
          <div style={{ width: '35%' }} className='me-3'>
            <div className='d-flex'>
              <div className='flex-grow-1'>
                <Controller
                  control={control}
                  name='descripcion'
                  render={({ field: { onChange } }) => (
                    <FormTextArea
                      nombreCampo='descripcion'
                      register={register}
                      nombre='Descripción'
                      errors={errors}
                      isObligatorio={true}
                      placeholder='Escribe una descripción'
                      maxLength={1500}
                      rows={1}
                      style={{ resize: 'vertical', maxHeight: '120px' }}
                      onChange={(e: any) => {
                        onChange(e)
                        generarTextoDescripcionTarea()
                        setDescripcionCondicionante(getValues('descripcion'))
                      }}
                    />
                  )}
                />
              </div>
            </div>
          </div>
          <div style={{ width: '15%' }} className='me-3'>
            <div className='d-flex'>
              <div className='flex-grow-1'>
                <Controller
                  control={control}
                  name='estatus'
                  render={({ field: { onChange, value } }) => (
                    <FormSelect
                      isDisabled={!isModoEditar || !usuarioPermiso}
                      value={value}
                      nombre='Estatus'
                      nombreCampo='estatus'
                      defaultValue
                      isObligatorio={true}
                      elementos={
                        isModoEditar
                          ? catEstatus
                          : catEstatus?.filter(
                              (e) =>
                                e?.valor !== AppConstantes.CVE_ESTATUS_CONDICIONANTE_COMPLETADA &&
                                e?.valor !== AppConstantes.CVE_ESTATUS_CONDICIONANTE_VENCIDA
                            )
                      }
                      onChange={(e: Opcion) => {
                        onChange(e)
                        // En creacion, cambiar estatus puede invalidar tareas autogeneradas.
                        !isModoEditar && borrarElementosTareasNoManuales()
                      }}
                      placeholder='Ej. Por iniciar'
                      errors={errors}
                    />
                  )}
                />
              </div>
            </div>
          </div>

          {!fechaVencimientoAutorizacion && !isModoEditar ? (
            <div style={{ width: '10%' }} className='me-3'>
              <Controller
                control={control}
                name='cantidadPeriodos'
                render={({ field: { onChange } }) => (
                  <FormInput
                    disabled={!usuarioPermiso}
                    maxLength={3}
                    min={0}
                    tipo='number'
                    className='mb-5'
                    nombreCampo='cantidadPeriodos'
                    nombre='Cantidad'
                    register={register}
                    errors={errors}
                    placeholder='Ej. 5'
                    onChange={(e: number) => {
                      onChange(e)
                      // Si no hay fecha de vencimiento en autorizacion, se pide al usuario la
                      // cantidad de periodos para calcular el calendario de tareas.
                      calcularCantidadTareas(periodicidadCondicionante)
                      setPeriodicidadCondicionante(getValues('periodicidad'))
                      borrarElementosTareasNoManuales()
                    }}
                  />
                )}
              />
            </div>
          ) : (
            ''
          )}

          {!isModoEditar ? (
            <div style={{ width: '15%' }}>
              <div className='d-flex'>
                <div className='flex-grow-1'>
                  <Controller
                    control={control}
                    name='periodicidad'
                    render={({ field: { onChange, value } }) => (
                      <FormSelect
                        nombre='Periodicidad'
                        isObligatorio={true}
                        value={value}
                        nombreCampo='periodicidad'
                        elementos={catPeriodicidad}
                        onChange={(e: Opcion) => {
                          onChange(e)
                          // Seleccionar periodicidad (y cantidadPeriodos) genera tareas.
                          calcularCantidadTareas(e)
                          setPeriodicidadCondicionante(e)
                          borrarElementosTareasNoManuales()
                        }}
                        placeholder={'Ej. Mensual'}
                        defaultValue={''}
                        errors={errors}
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className='col-1'>
              <label className='form-label fs-4'>Periodicidad</label>
              <h4 className='mt-3 mx-4'>
                {condicionante &&
                  AppMapas.MAPA_PERIODICIDAD_CONDICIONANTES_DESCRIPCIONES.get(
                    condicionante?.periodicidad
                  )}
              </h4>
            </div>
          )}
        </div>

        <div className='col-12 d-flex mb-5'></div>

        <div className='col-12'></div>
      </div>
    </>
  )
}
